import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import i18n from 'i18next'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { cloneDeep } from 'lodash'
import Modal from '../../UniformComponents/Modal'
import Input from '../form/Input'
import Button from '../../UniformComponents/Button'
import ModalFooter from '../../UniformComponents/ModalFooter'
import ModalHeader from '../../UniformComponents/ModalHeader'
import Checkbox from '../form/Checkbox'
import DashboardDelete from './modal/DashboardDelete'

@observer
class ManageDashboardsModal extends Component {
    constructor(props) {
        super(props)
        const {
            dashStore,
        } = props
        const { oId, appStore } = dashStore
        const initPrivacy = oId === 3 ? 'public' : 'private'
        const dashboards = this.getDashboards(initPrivacy)
            .filter((d) => d.clientId === appStore.userData.clientId)

        const otherDashboards = oId === 3 ? [] : this.getDashboards('public')
            .filter((d) => d.clientId === appStore.userData.clientId)

        this.state = {
            privacy: initPrivacy,
            dashboards: JSON.parse(JSON.stringify(dashboards)),
            otherDashboards: JSON.parse(JSON.stringify(otherDashboards)),
            error: false,
            updatedDashbaords: [],
            deleteDashboards: [],
            deleteDashboardCheckId: undefined,
        }
    }

    changePrivacy = (newPrivacy) => {
        const { dashboards, otherDashboards, privacy } = this.state
        if (privacy === newPrivacy) return
        this.setState({
            privacy: newPrivacy,
            dashboards: otherDashboards,
            otherDashboards: dashboards,
            error: false,
        })
    }

    save = async () => {
        const {
            dashboards, updatedDashbaords, otherDashboards, deleteDashboards,
        } = this.state
        const { dashStore, onClose } = this.props
        const { appStore, init } = dashStore

        const isError = dashboards.reduce((err, d) => !d.name || err, false)

        if (isError) {
            this.setState({ error: true })
            return
        }

        try {
            const dashboardsToUpdate = [...dashboards, ...otherDashboards]
                .filter((d) => updatedDashbaords.includes(d.dashboardId))
            if (dashboardsToUpdate.length) {
                await Promise.all(
                    dashboardsToUpdate.map((d) => (
                        appStore.http.put(`/dashboard/${d.dashboardId}`, JSON.stringify({ ...d, widgets: JSON.stringify(d.widgets) })))),
                )
            }

            if (deleteDashboards.length) {
                await Promise.all(
                    deleteDashboards.map((dId) => (
                        appStore.http.delete(`/dashboard/${dId}`))),
                )
            }

            init(true)
            appStore.addNotification(null, 'Dashboards Updated', 'success')
        } catch (err) {
            appStore.addNotification(null, 'Unable to update dashboards', 'error')
        }
        onClose()
    }

    cancel = () => {
        const { onClose } = this.props
        onClose()
    }

    getDashboards = (privacyOverride) => {
        const { dashStore } = this.props
        const { appStore, dashboards, oId } = dashStore
        const { clientId } = appStore.userData
        const deleteDashboards = R.pathOr([], ['state', 'deleteDashboards'], this)
        const privacy = R.pathOr(privacyOverride, ['state', 'privacy'], this)
        const dashboardOptions = dashboards.filter((d) => (
            d.clientId === clientId && d.oId === oId
            && d.dashboardId && !deleteDashboards.includes(d.dashboardId)
            && ((privacyOverride || privacy) === 'private' ? d.recordId !== 0 : (d.recordId === 0 || oId === 3))
        )).sort((a, b) => {
            const aPriority = R.pathOr(-1, ['widgets', 'defaultDashSettings', 'priority'], a)
            const bPriority = R.pathOr(-1, ['widgets', 'defaultDashSettings', 'priority'], b)
            return aPriority - bPriority
        })
        return dashboardOptions
    }

    reorderDashboards = (result) => {
        const { dashboards, updatedDashbaords } = this.state
        const startIndex = result.source.index
        const endIndex = result.destination.index
        const newDashboards = [...dashboards]
        newDashboards.splice(endIndex, 0, newDashboards.splice(startIndex, 1)[0])
        const newUpdatedDashboards = dashboards.reduce((toUpdate, d) => (
            updatedDashbaords.includes(d.dashboardId)
                ? updatedDashbaords
                : [...toUpdate, d.dashboardId]
        ),
        updatedDashbaords)
        this.setState({
            dashboards: newDashboards.map((d, i) => ({
                ...d,
                widgets: {
                    ...d.widgets,
                    defaultDashSettings: { ...R.pathOr({}, ['widgets', 'defaultDashSettings'], d), priority: i },
                },
            })),
            updatedDashbaords: newUpdatedDashboards,
        })
    }

    renameDashboard = ({ name, value }) => {
        const { dashboards, updatedDashbaords } = this.state
        const dashboardId = Number(name)
        const dashboardIndex = dashboards.findIndex((d) => d.dashboardId === dashboardId)
        const dashboard = dashboards[dashboardIndex]
        if (dashboard.name === value) return
        dashboard.name = value
        const isTab = R.pathOr(false, ['widgets', 'defaultDashSettings', 'level'], dashboard)
        dashboard.widgets.defaultDashSettings = {
            ...dashboard.widgets.defaultDashSettings,
            level: isTab ? value : undefined,
        }
        dashboards[dashboardIndex] = dashboard
        if (!updatedDashbaords.includes(dashboardId)) updatedDashbaords.push(dashboardId)
        this.setState({ dashboards, updatedDashbaords, error: false })
    }

    setIsTab = ({ name, value }) => {
        const { dashboards, updatedDashbaords } = this.state
        const dashboardId = Number(name)
        const dashboardIndex = dashboards.findIndex((d) => d.dashboardId === dashboardId)
        const dashboard = dashboards[dashboardIndex]
        dashboard.widgets.defaultDashSettings = {
            ...dashboard.widgets.defaultDashSettings,
            level: value ? dashboards[dashboardIndex].name : undefined,
        }
        if (!updatedDashbaords.includes(dashboardId)) updatedDashbaords.push(dashboardId)
        this.setState({ dashboards, updatedDashbaords })
    }

    deleteDashboard = (dashboardId) => {
        const { deleteDashboards, dashboards } = this.state
        const dashboardIndex = dashboards.findIndex((d) => d.dashboardId === dashboardId)
        deleteDashboards.push(dashboardId)
        dashboards.splice(dashboardIndex, 1)
        this.setState({ deleteDashboards, dashboards })
    }

    renderDashboards = () => {
        const { dashStore } = this.props
        const { appStore } = dashStore
        const { dashboards } = this.state
        return dashboards.map((dashboard, i) => (
            <Draggable
                key={dashboard.dashboardId}
                draggableId={dashboard.dashboardId.toString()}
                index={i}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                    >
                        <div className="dashboardRow">
                            <i className="fa-light fa-arrows-maximize" style={{ fontSize: '20px', width: '30px' }} />
                            <Input
                                name={dashboard.dashboardId}
                                onBlur={this.renameDashboard}
                                defaultValue={dashboard.name}
                                key={`input_${dashboard.dashboardId}`}
                            />
                            {appStore.isExploriAdmin() ? (
                                <Checkbox
                                    name={dashboard.dashboardId}
                                    onBlur={this.setIsTab}
                                    checked={!!R.pathOr(false, ['widgets', 'defaultDashSettings', 'level'], dashboard)}
                                    style={{ width: '40px', flex: 'none', margin: '0 30px 0 30px' }}
                                />
                            ) : null}
                            <i
                                className="fa-regular fa-circle-xmark"
                                key={dashboard.dashboardId}
                                onClick={() => this.setState({ deleteDashboardCheckId: dashboard.dashboardId })}
                            />
                        </div>
                    </div>
                )}
            </Draggable>
        ))
    }

    deleteConfirmation = () => {
        const { deleteDashboardCheckId, dashboards } = this.state
        return (
            <DashboardDelete
                onDelete={() => {
                    this.setState({ deleteDashboardCheckId: undefined })
                    this.deleteDashboard(deleteDashboardCheckId)
                }}
                onClose={() => this.setState({ deleteDashboardCheckId: undefined })}
                dashStore={{
                    dashboard:
                    {
                        name: deleteDashboardCheckId
                            ? dashboards.find((d) => d.dashboardId === deleteDashboardCheckId).name
                            : null,
                    },
                }}
                show={!!deleteDashboardCheckId}
            />
        )
    }

    render() {
        const {
            showManageDashboardsModal, onClose, dashStore,
        } = this.props
        const { oId, getChartScope, appStore } = dashStore
        const { privacy, error } = this.state
        const currentLevelName = getChartScope(oId)

        return (
            <Modal
                show={showManageDashboardsModal}
                onClose={onClose}
                cssClass="tabCreationModal"
            >
                <ModalHeader cssClass="chartAdvancedPreview-header " style={{ textAlign: 'center' }}>
                    <h2 className="title">
                        {i18n.t('dashboards.manageDashboards.title')}
                    </h2>
                    <div className="subTitle">
                        {i18n.t('dashboards.manageDashboards.subtitle', { 1: currentLevelName })}
                        {oId > 3
                            ? (` ${i18n.t('dashboards.manageDashboards.subtitle2', { 1: currentLevelName })}`)
                            : null }
                    </div>
                </ModalHeader>
                <div className="body">
                    <div className="privacyTabs" style={{ display: 'flex', marginBottom: '20px' }}>
                        {oId !== 3
                            ? (
                                <Fragment>
                                    <div
                                        className={`private privacyTab ${privacy === 'private' ? 'active' : undefined}`}
                                        style={{ marginRight: '15px', cursor: 'pointer', textDecoration: privacy === 'private' ? 'underline' : undefined }}
                                        onClick={() => this.changePrivacy('private')}
                                    >
                                        {i18n.t('dashboards.manageDashboards.privacySettingsPrivate', { 1: currentLevelName })}
                                    </div>
                                    {appStore.isMasterSuper()
                                        ? (
                                            <div
                                                className={`public privacyTab ${privacy === 'public' ? 'active' : undefined}`}
                                                style={{ cursor: 'pointer', textDecoration: privacy === 'public' ? 'underline' : undefined }}
                                                onClick={() => this.changePrivacy('public')}
                                            >
                                                {i18n.t('dashboards.manageDashboards.privacySettingsPublic', { 1: currentLevelName })}
                                            </div>
                                        )
                                        : null}
                                </Fragment>
                            )
                            : null }
                    </div>
                    <hr />
                    <div className="headings" style={{ display: 'flex', fontWeight: 'bold' }}>
                        <div className="heading" style={{ flex: '1', marginLeft: '50px' }}>
                            {i18n.t('dashboard_name')}
                        </div>
                        {appStore.isExploriAdmin() ? (
                            <div className="heading" style={{ width: '100px', textAlign: 'center' }}>
                                {i18n.t('set_as_tab')}

                            </div>
                        ) : null}
                        <div className="heading" style={{ width: '80px', textAlign: 'center', marginLeft: '20px' }}>
                            {i18n.t('delete')}
                        </div>
                    </div>
                    <DragDropContext onDragEnd={this.reorderDashboards}>
                        <Droppable droppableId="droppable">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {this.renderDashboards()}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    {error
                        ? (
                            <div className="errorMessage" style={{ color: '#E04F57', marginTop: '20px', fontSize: '18px' }}>
                                <i className="fa-regular fa-circle-exclamation" />
                                {' '}
                                {i18n.t('dashboards.manageDashboards.errorMsg')}
                            </div>
                        )
                        : null}
                </div>
                <ModalFooter>
                    <div className="footerControls">
                        <Button
                            label="Cancel"
                            cssClass="tertiary regular"
                            style={{
                                width: '134px', fontWeight: 600, textAlign: 'center',
                            }}
                            onClick={onClose}
                        />
                        <Button
                            label="Save"
                            cssClass="primary regular"
                            style={{ width: '134px', fontWeight: 600, textAlign: 'center' }}
                            onClick={this.save}
                        />
                    </div>
                </ModalFooter>
                {this.deleteConfirmation()}
            </Modal>
        )
    }
}

export default ManageDashboardsModal
